/**
 * Root Router Configuration
 *
 * Purpose: Main routing configuration with lazy loading and transitions
 * Features:
 * - Route guards
 * - Code splitting
 * - Framer Motion transitions
 */

import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { ErrorBoundary } from '@/features/common/components/ErrorBoundary'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { Toast } from '@/features/common/components/Toast'
import { AppRoutes } from './AppRoutes'
import { AdminRoutes } from './AdminRoutes'

// Lazy load auth pages
const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage }))
)
const RegisterPage = lazy(() =>
  import('@/features/auth/pages/RegisterPage').then(m => ({ default: m.RegisterPage }))
)
const AuthLayout = lazy(() => import('@/layouts/AuthLayout').then(m => ({ default: m.AuthLayout })))

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}

export function AppRouter() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Toast />
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
            <Routes>
              {/* Auth Routes */}
              <Route element={<AuthLayout />}>
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/register" element={<RegisterPage />} />
              </Route>

              {/* App Routes (User-facing) */}
              <Route path="/app/*" element={<AppRoutes />} />

              {/* Admin Routes */}
              <Route path="/admin/*" element={<AdminRoutes />} />

              {/* Default redirect - always start at login */}
              <Route path="/" element={<Navigate to="/auth/login" replace />} />
              {/* Catch all - redirect to login if route doesn't exist */}
              <Route path="*" element={<Navigate to="/auth/login" replace />} />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
